import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookings, tickets } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Badge, Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { SegmentedControl, Select, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { AvailabilityPicker, type AvailabilityPick } from '../../bookings/components/AvailabilityPicker';
import { BOOKINGS_KEY, bookingErrorText, useStaff } from '../../bookings/lib/data';
import { TICKETS_KEY, personName, ticketErrorText, type Lookups } from '../lib/data';
import { fromLocalInput } from '../lib/time';

const MUTED = 'var(--rr-color-text-muted)';

export function taskKindLabel(t: Translate, kind: string): string {
  return t(`tickets.task.kind.${kind}`, kind);
}

/** A tasks error: a booking refusal in the bookings' words, anything else in the tickets'. */
function taskErrorText(t: Translate, e: unknown): string {
  return bookings.bookingErrorCode(e) ? bookingErrorText(t, e) : ticketErrorText(t, e);
}

/** The ticket's tasks: open ones by due time, then the done ones with their outcome. */
export function TasksPanel({ ticket, lk, tz, onChanged }: { ticket: tickets.Ticket; lk: Lookups; tz: string; onChanged: () => void }) {
  const t = useT();
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [finishing, setFinishing] = useState<tickets.Task | null>(null);
  const [showDone, setShowDone] = useState(false);
  const now = Date.now();

  const open = ticket.tasks.filter((x) => !x.done_at).sort((a, b) => (a.due_at ?? '9').localeCompare(b.due_at ?? '9'));
  const done = ticket.tasks.filter((x) => x.done_at).sort((a, b) => (b.done_at ?? '').localeCompare(a.done_at ?? ''));

  const reopen = useMutation({
    mutationFn: (task: tickets.Task) => tickets.updateTask(ticket.id, task.id, { done: false }),
    onSuccess: onChanged,
    onError: (e) => notifyError(ticketErrorText(t, e), t('tickets.tasks.title'))
  });

  const who = (id: string | null) => (id ? (id === user?.id ? t('tickets.me') : lk.personLabel(id) || t('common.unknown')) : t('tickets.unassigned'));

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group gap="sm" justify="space-between">
          <Text role="subtitle" fw={600}>{t('tickets.tasks.title')}</Text>
          {!ticket.merged_into_id && <Button size="xs" variant="subtle" onClick={() => setAdding(true)}>{t('tickets.tasks.add')}</Button>}
        </Group>
        {open.length === 0 && <Text role="bodySmall" c={MUTED}>{t('tickets.tasks.noneOpen')}</Text>}
        {open.map((x) => {
          const overdue = !!x.due_at && Date.parse(x.due_at) < now;
          return (
            <div key={x.id} className={`rr-tickets__task${overdue ? ' is-overdue' : ''}`}>
              <Group gap="xs" justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Group gap="xs" wrap="wrap">
                    <Badge size="xs" variant="light">{taskKindLabel(t, x.kind)}</Badge>
                    <Text role="bodySmall" fw={600}>{x.title}</Text>
                  </Group>
                  {x.question && <Text role="caption" c={MUTED}>{t('tickets.task.asked', { question: x.question })}</Text>}
                  <Text role="caption" c={overdue ? 'var(--rr-color-error)' : MUTED}>
                    {x.due_at
                      ? (overdue ? t('tickets.task.overdueSince', { when: formatRelative(x.due_at) }) : t('tickets.task.dueWhen', { when: formatDateTime(x.due_at) }))
                      : t('tickets.task.noDue')}
                    {' · '}{who(x.assignee_user_id)}
                  </Text>
                  {x.booking_id && <Link to={`/bookings/${encodeURIComponent(x.booking_id)}`}>{t('tickets.task.openBooking')}</Link>}
                </Stack>
                {!ticket.merged_into_id && <Button size="xs" variant="default" onClick={() => setFinishing(x)}>{t('tickets.task.markDone')}</Button>}
              </Group>
            </div>
          );
        })}
        {done.length > 0 && (
          <div>
            <Button size="xs" variant="subtle" onClick={() => setShowDone((s) => !s)} aria-expanded={showDone}>
              {showDone ? t('tickets.tasks.hideDone') : t('tickets.tasks.showDone', { count: done.length })}
            </Button>
          </div>
        )}
        {showDone && done.map((x) => (
          <div key={x.id} className="rr-tickets__task is-done">
            <Group gap="xs" justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Group gap="xs" wrap="wrap">
                  <Badge size="xs" variant="light" color="gray">{taskKindLabel(t, x.kind)}</Badge>
                  <Text role="bodySmall" style={{ textDecoration: 'line-through' }}>{x.title}</Text>
                </Group>
                <Text role="caption" c={MUTED}>{t('tickets.task.doneBy', { who: x.done_by || t('tickets.timeline.someone'), when: formatDateTime(x.done_at) })}</Text>
                {x.outcome && <Text role="caption">{t('tickets.task.outcomeIs', { outcome: x.outcome })}</Text>}
              </Stack>
              {!ticket.merged_into_id && (
                <Button size="xs" variant="subtle" loading={reopen.isPending && reopen.variables?.id === x.id} onClick={() => reopen.mutate(x)}>
                  {t('tickets.task.reopen')}
                </Button>
              )}
            </Group>
          </div>
        ))}
      </Stack>

      <TaskModal opened={adding} onClose={() => setAdding(false)} ticket={ticket} lk={lk} tz={tz} onCreated={onChanged} />
      <DoneModal task={finishing} ticketId={ticket.id} onClose={() => setFinishing(null)} onDone={onChanged} />
    </Card>
  );
}

export function DoneModal({ task, ticketId, onClose, onDone }: { task: tickets.Task | null; ticketId: string; onClose: () => void; onDone: () => void }) {
  const t = useT();
  const [outcome, setOutcome] = useState('');
  useEffect(() => { if (task) setOutcome(''); }, [task]);
  const finish = useMutation({
    mutationFn: () => tickets.updateTask(ticketId, task!.id, { done: true, outcome: outcome.trim() || undefined }),
    onSuccess: () => { notifySuccess(t('tickets.task.doneToast', { title: task!.title }), t('tickets.tasks.title')); onDone(); onClose(); }
  });
  return (
    <Modal opened={!!task} onClose={onClose} title={t('tickets.task.doneTitle')} size="md">
      {task && (
        <form onSubmit={(e) => { e.preventDefault(); finish.mutate(); }}>
          <Stack gap="md">
            <Text role="bodySmall">{task.title}</Text>
            <Textarea label={t('tickets.task.outcome')} value={outcome} onChange={(e) => setOutcome(e.currentTarget.value)} minRows={3} maxLength={2000}
              description={t('tickets.task.outcomeHint')} data-autofocus />
            {finish.error && <Alert color="error" role="alert">{ticketErrorText(t, finish.error)}</Alert>}
            <Group gap="sm" justify="flex-end">
              <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
              <Button type="submit" loading={finish.isPending}>{t('tickets.task.markDone')}</Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}

/** The first address the ticket's fields hold: where a call-out goes unless somebody types another. */
function addressFrom(ticket: tickets.Ticket, lk: Lookups): string {
  const type = lk.typeById.get(ticket.type.id);
  for (const f of type?.fields ?? []) {
    const v = ticket.fields?.[f.key];
    if (f.type === 'address' && typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function TaskModal({ opened, onClose, ticket, lk, tz, onCreated }: {
  opened: boolean;
  onClose: () => void;
  ticket: tickets.Ticket;
  lk: Lookups;
  tz: string;
  onCreated: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [kind, setKind] = useState<tickets.TaskKind>('call');
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState('');
  const [question, setQuestion] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [pick, setPick] = useState<AvailabilityPick | null>(null);
  const [staffId, setStaffId] = useState('');
  const [address, setAddress] = useState('');
  const [tried, setTried] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!opened) return;
    setKind('call');
    setTitle(t('tickets.task.defaultTitle.call'));
    setTitleTouched(false);
    setDue('');
    setAssignee(user?.id ?? '');
    setQuestion('');
    setServiceId('');
    setPick(null);
    setStaffId('');
    setAddress(addressFrom(ticket, lk));
    setTried(false);
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!titleTouched) setTitle(t(`tickets.task.defaultTitle.${kind}`)); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const servicesQ = useQuery({
    queryKey: [BOOKINGS_KEY, 'services', false],
    queryFn: () => bookings.listServices(),
    enabled: opened && kind === 'callout',
    staleTime: 30_000
  });
  const services = useMemo(() => {
    const all = (servicesQ.data?.data ?? []).filter((s) => s.active);
    // Call-outs first: that is what this task is for; the others still book.
    return [...all].sort((a, b) => Number(b.kind === 'callout') - Number(a.kind === 'callout') || a.sort_order - b.sort_order);
  }, [servicesQ.data]);
  const service = services.find((s) => s.id === serviceId) ?? null;
  const staffQ = useStaff();
  const staffName = (id: string) => staffQ.data?.data.find((p) => p.id === id)?.name ?? t('common.unknown');
  useEffect(() => { if (kind === 'callout' && !serviceId && services[0]) setServiceId(services[0].id); }, [kind, services, serviceId]);
  useEffect(() => { setPick(null); setStaffId(''); }, [serviceId]);

  const problem = !title.trim() ? 'title'
    : kind === 'question' && !question.trim() ? 'question'
    : kind === 'callout' && (!service || !pick) ? 'slot'
    : kind === 'callout' && service?.location_kind !== 'business' && service?.kind === 'callout' && !address.trim() ? 'address'
    : null;

  const create = useMutation({
    mutationFn: () => tickets.createTask(ticket.id, {
      kind,
      title: title.trim(),
      due_at: kind !== 'callout' ? fromLocalInput(due, tz) ?? undefined : undefined,
      assignee_user_id: assignee || undefined,
      question: kind === 'question' ? question.trim() : undefined,
      booking: kind === 'callout' && service && pick ? {
        service_id: service.id,
        starts_at: pick.starts_at,
        staff_id: staffId || pick.staff_id || undefined,
        address: address.trim() || undefined
      } : undefined
    }),
    onSuccess: () => {
      notifySuccess(t('tickets.task.created', { title: title.trim() }), t('tickets.tasks.title'));
      if (kind === 'callout') void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
      void qc.invalidateQueries({ queryKey: [TICKETS_KEY, 'tasks'] });
      onCreated();
      onClose();
    },
    onError: (e) => {
      if (bookings.bookingErrorCode(e) === bookings.SLOT_TAKEN || bookings.bookingErrorCode(e) === bookings.FULL) {
        setPick(null);
        setRefreshKey((k) => k + 1);
      }
    }
  });

  function submit() {
    setTried(true);
    if (problem) return;
    create.mutate();
  }

  const people = [
    { value: '', label: t('tickets.unassigned') },
    ...(user ? [{ value: user.id, label: t('tickets.me') }] : []),
    ...lk.people.filter((p) => p.id !== user?.id).map((p) => ({ value: p.id, label: personName(p) }))
  ];

  return (
    <Modal opened={opened} onClose={onClose} title={t('tickets.tasks.add')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Stack gap="md" data-testid="task-modal">
          <Stack gap="xs">
            <Text role="inputLabel">{t('tickets.task.kind')}</Text>
            <SegmentedControl
              value={kind}
              onChange={(v) => setKind(v as tickets.TaskKind)}
              data={tickets.TASK_KINDS.map((k) => ({ value: k, label: taskKindLabel(t, k) }))}
            />
            <Text role="caption" c={MUTED}>{t(`tickets.task.kindHint.${kind}`)}</Text>
          </Stack>

          <TextInput label={t('tickets.task.title')} value={title} required maxLength={200}
            onChange={(e) => { setTitle(e.currentTarget.value); setTitleTouched(true); }}
            error={tried && problem === 'title' ? t('tickets.task.error.title') : undefined} />

          {kind === 'question' && (
            <Textarea label={t('tickets.task.question')} value={question} required minRows={3} maxLength={1000}
              onChange={(e) => setQuestion(e.currentTarget.value)}
              description={t('tickets.task.questionHint')}
              error={tried && problem === 'question' ? t('tickets.task.error.question') : undefined} />
          )}

          {kind === 'callout' && (
            <Stack gap="sm">
              {servicesQ.error && <Alert color="error" role="alert">{bookingErrorText(t, servicesQ.error)}</Alert>}
              {servicesQ.isSuccess && services.length === 0 ? (
                <Alert color="info">
                  <Group gap="sm" justify="space-between" wrap="wrap">
                    <span>{t('tickets.task.noServices')}</span>
                    <Link to="/bookings/services">{t('tickets.task.setUpServices')}</Link>
                  </Group>
                </Alert>
              ) : (
                <Select
                  label={t('tickets.task.service')}
                  value={serviceId}
                  onChange={(e) => setServiceId(e.currentTarget.value)}
                  data={services.map((s) => ({ value: s.id, label: s.name }))}
                />
              )}
              {service && (
                <AvailabilityPicker service={service} tz={tz} value={pick} onChange={setPick} refreshKey={refreshKey} />
              )}
              {tried && problem === 'slot' && <Text role="caption" c="var(--rr-color-error)">{t('tickets.task.error.slot')}</Text>}
              {pick && (pick.staff_ids?.length ?? 0) > 1 && (
                <Select
                  label={t('tickets.task.person')}
                  value={staffId}
                  onChange={(e) => setStaffId(e.currentTarget.value)}
                  data={[{ value: '', label: t('tickets.task.anyPerson') }, ...(pick.staff_ids ?? []).map((id) => ({ value: id, label: staffName(id) }))]}
                />
              )}
              <Textarea label={t('tickets.task.address')} value={address} minRows={2} maxLength={300}
                onChange={(e) => setAddress(e.currentTarget.value)}
                description={t('tickets.task.addressHint')}
                error={tried && problem === 'address' ? t('tickets.task.error.address') : undefined} />
            </Stack>
          )}

          {kind !== 'callout' && (
            <TextInput type="datetime-local" label={t('tickets.task.due')} value={due} onChange={(e) => setDue(e.currentTarget.value)}
              description={t('tickets.field.dueHint', { zone: tz })} />
          )}
          <Select label={t('tickets.field.assignee')} value={assignee} onChange={(e) => setAssignee(e.currentTarget.value)} data={people} />

          {create.error && <Alert color="error" role="alert">{taskErrorText(t, create.error)}</Alert>}
          <Group gap="sm" justify="flex-end">
            <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={create.isPending}>{kind === 'question' ? t('tickets.task.askNow') : kind === 'callout' ? t('tickets.task.book') : t('tickets.tasks.add')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
